export function queryFocusable(parent: HTMLElement, last = false) {
	const focusable = parent.querySelectorAll<typeof parent>(
		'a, button, input, textarea, select, details',
	);

	if (focusable.length === 0) return null;

	if (last === true) {
		return focusable[focusable.length - 1];
	}

	return focusable[0];
}

export function duration(ms: number): number {
	return 'matchMedia' in window &&
		matchMedia('(prefers-reduced-motion: reduce)').matches
		? 1
		: ms * 1.1;
}

/**
 * Callbacks
 */

type Callback<T = undefined> = (argument: T) => void;
export function makeCallback<T>() {
	let callbacksWereInvoked = false;
	let invokationArgument: T;
	const callbacks: Callback<T>[] = [];

	return {
		add(callback: Callback<T>) {
			callbacks.push(callback);
			if (callbacksWereInvoked) {
				callback(invokationArgument);
			}
		},
		invoke(argument: T) {
			callbacks.forEach((callback) => callback(argument));
			invokationArgument = argument;
			callbacksWereInvoked = true;
		},
	};
}

/**
 * Trap focus
 */

const handleKeyUp = (el: HTMLElement) => (ev: KeyboardEvent) => {
	if (
		ev.key === 'Tab' &&
		ev.target instanceof HTMLElement &&
		!el.contains(ev.target)
	) {
		if (ev.shiftKey) {
			queryFocusable(el, true)?.focus();
		} else {
			queryFocusable(el)?.focus();
		}
	}
};

let trapMap: {
	el: HTMLElement;
	handleKeyUp: (ev: KeyboardEvent) => void;
}[] = [];

export function trapFocus(el: HTMLElement) {
	let trap = trapMap.find(({ el: element }) => element === el);

	if (!trap) {
		trap = { el, handleKeyUp: handleKeyUp(el) };
		trapMap.push(trap);
	}

	document.addEventListener('keyup', trap.handleKeyUp);
}

export function releaseFocus(el: HTMLElement) {
	let trap = trapMap.find(({ el: element }) => element === el);
	if (trap) {
		document.removeEventListener('keyup', trap.handleKeyUp);
	}
}
